<script>
import { required, minValue, maxValue } from "vuelidate/lib/validators";
import api from "@/helpers/apirest/api";

export default {
    props: {
        integrationId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            submitted: false,
            form: {
                dateFrom: null,
                dateTo: null,
                adults: 1,
                children: 0,
                childrenAges: [],
                activities: ''
            },
            maxAdults: 99,
            maxChildren: 10,
            childrenAgeOptions: Array.from({ length: 18 }, (_, i) => ({ value: i, text: `${i} años` }))
        };
    },
    validations: {
        form: {
            dateFrom: { required },
            dateTo: { required },
            adults: { 
                required, 
                minValue: minValue(1), 
                maxValue: maxValue(99) 
            },
            children: { 
                minValue: minValue(0), 
                maxValue: maxValue(10) 
            },
            activities: { required }
        }
    },
    computed: {
        isDateRangeValid() {
            if (!this.form.dateFrom || !this.form.dateTo) {
                return true; // Let vuelidate handle required validation
            }
            return new Date(this.form.dateTo) >= new Date(this.form.dateFrom);
        },
        isActivitiesValid() {
            if (!this.form.activities) return true;
            const activities = this.form.activities.split(',').map(code => code.trim());
            return activities.every(code => code.length > 0);
        }
    },
    watch: {
        'form.children'(newValue) {
            this.updateChildrenAges(newValue);
        }
    },
    methods: {
        updateChildrenAges(childrenCount) {
            const currentCount = this.form.childrenAges.length;
            
            if (childrenCount > currentCount) {
                // Add new age selectors
                for (let i = currentCount; i < childrenCount; i++) {
                    this.form.childrenAges.push(0);
                }
            } else if (childrenCount < currentCount) {
                // Remove excess age selectors
                this.form.childrenAges.splice(childrenCount);
            }
        },
        async consultar() {
            this.submitted = true;
            this.$v.$touch();
            
            if (this.$v.$invalid || !this.isDateRangeValid || !this.isActivitiesValid) {
                return;
            }

            // Emit preview start event
            this.$emit('preview-start');

            try {
                const requestData = {
                    date_from: this.form.dateFrom,
                    date_to: this.form.dateTo,
                    adults: this.form.adults,
                    children: this.form.children,
                    children_ages: this.form.childrenAges,
                    activities: this.form.activities.split(',').map(code => code.trim())
                };

                const response = await api.post({
                    url: `integrations/${this.integrationId}/preview`,
                    data: requestData,
                    config: {
                        withLoading: true
                    }
                });
                
                // Emit event to parent component with results
                this.$emit('preview-results', response?.data?.data);
               
            } catch (error) {
                console.error('Error fetching preview:', error);
                
                // Emit error event to parent component
                this.$emit('preview-error');
            }
        },
        resetForm() {
            this.form = {
                dateFrom: null,
                dateTo: null,
                adults: 1,
                children: 0,
                childrenAges: [],
                activities: ''
            };
            this.submitted = false;
            this.$v.$reset();
        }
    }
};
</script>

<template>
    <div class="card h-100">
        <div class="card-body">
            <h4 class="card-title mb-4">Parámetros de búsqueda</h4>
            
            <div class="alert alert-info mb-4">
                <h6 class="alert-heading">
                    <i class="mdi mdi-information mr-2"></i>
                    Información
                </h6>
                <p class="mb-0">Completa los parámetros de búsqueda para obtener una previsualización de las actividades disponibles en HotelBeds.</p>
            </div>
            
            <form @submit.prevent="consultar">
                <div class="row">
                    <!-- Date Range -->
                    <div class="form-group col-md-6">
                        <label for="dateFrom">Fecha desde</label>
                        <input 
                            id="dateFrom"
                            v-model="form.dateFrom"
                            type="date"
                            class="form-control"
                            :class="{ 'is-invalid': $v.form.dateFrom.$error && submitted }"
                        />
                        <div v-if="!$v.form.dateFrom.required && submitted" class="invalid-feedback">
                            Fecha desde es requerida
                        </div>
                    </div>
                    
                    <div class="form-group col-md-6">
                        <label for="dateTo">Fecha hasta</label>
                        <input 
                            id="dateTo"
                            v-model="form.dateTo"
                            type="date"
                            class="form-control"
                            :class="{ 'is-invalid': $v.form.dateTo.$error && submitted }"
                        />
                        <div v-if="!$v.form.dateTo.required && submitted" class="invalid-feedback">
                            Fecha hasta es requerida
                        </div>
                        <div v-if="!isDateRangeValid && submitted" class="invalid-feedback">
                            La fecha final no puede ser inferior a la fecha inicial
                        </div>
                    </div>

                    <!-- Adults and Children -->
                    <div class="form-group col-md-6">
                        <label for="adults">Número de adultos</label>
                        <input 
                            id="adults"
                            v-model.number="form.adults"
                            type="number"
                            min="1"
                            :max="maxAdults"
                            class="form-control"
                            :class="{ 'is-invalid': $v.form.adults.$error && submitted }"
                        />
                        <div v-if="!$v.form.adults.required && submitted" class="invalid-feedback">
                            Número de adultos es requerido
                        </div>
                        <div v-if="!$v.form.adults.minValue && submitted" class="invalid-feedback">
                            Mínimo 1 adulto
                        </div>
                        <div v-if="!$v.form.adults.maxValue && submitted" class="invalid-feedback">
                            Máximo {{ maxAdults }} adultos
                        </div>
                    </div>

                    <div class="form-group col-md-6">
                        <label for="children">Número de niños</label>
                        <input 
                            id="children"
                            v-model.number="form.children"
                            type="number"
                            min="0"
                            :max="maxChildren"
                            class="form-control"
                            :class="{ 'is-invalid': $v.form.children.$error && submitted }"
                        />
                        <div v-if="!$v.form.children.minValue && submitted" class="invalid-feedback">
                            Mínimo 0 niños
                        </div>
                        <div v-if="!$v.form.children.maxValue && submitted" class="invalid-feedback">
                            Máximo {{ maxChildren }} niños
                        </div>
                    </div>

                    <!-- Children Ages -->
                    <div class="form-group col-12" v-if="form.children > 0">
                        <label>Edades de los niños</label>
                        <div class="row">
                            <div class="col-md-3 col-sm-6" v-for="(age, index) in form.childrenAges" :key="index">
                                <select 
                                    v-model="form.childrenAges[index]"
                                    class="form-control mb-2"
                                >
                                    <option v-for="option in childrenAgeOptions" :key="option.value" :value="option.value">
                                        {{ option.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Activities -->
                    <div class="form-group col-12">
                        <label for="activities">Códigos de actividades</label>
                        <textarea 
                            id="activities"
                            v-model="form.activities"
                            class="form-control"
                            rows="3"
                            placeholder="Ingresa los códigos de actividades separados por comas (ej: ABC123, DEF456, GHI789)"
                            :class="{ 'is-invalid': ($v.form.activities.$error || !isActivitiesValid) && submitted }"
                        ></textarea>
                        <div v-if="!$v.form.activities.required && submitted" class="invalid-feedback">
                            Códigos de actividades es requerido
                        </div>
                        <div v-if="!isActivitiesValid && submitted" class="invalid-feedback">
                            Los códigos deben estar separados correctamente por comas
                        </div>
                        <small class="form-text text-muted">
                            Separa los códigos con comas (ej: ABC123, DEF456, GHI789)
                        </small>
                    </div>

                    <!-- Submit Button -->
                    <div class="form-group col-12">
                        <div class="d-flex gap-2">
                            <button 
                                type="submit"
                                class="btn btn-sm btn-primary waves-effect waves-light"
                                :disabled="$store.state.loading.loading"
                            >
                                <i class="mdi mdi-magnify mr-1"></i>
                                Consultar
                            </button>
                            <button 
                                type="button"
                                class="btn btn-sm btn-outline-secondary waves-effect waves-light"
                                @click="resetForm"
                                :disabled="$store.state.loading.loading"
                            >
                                <i class="mdi mdi-refresh mr-1"></i>
                                Limpiar
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template> 